import React, {useEffect, useMemo, useRef, useState} from "react";
import DOMPurify from "dompurify";
import axios from "axios";
import "./css/Tearsheet.css";
import NotesAndCommentary from "./NotesAndCommentary";
import {sanitizeAndHighlight, buildHighlightTerms, sectionContainsKeyword} from "./highlightHtml";

export default function ProductTearsheet({ productId, contextPath, onOpenProductPopup, onOpenVendorPopup, onOpenDataModal, onOpenFunctionalityModal, highlight, onGlossaryClick , mode = "profiler" }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [synopsisExpanded, setSynopsisExpanded] = useState(false);
    const [synopsisExpandAll, setSynopsisExpandAll] = useState(false);
    const [synopsisSectionCount, setSynopsisSectionCount] = useState(0);
    const [descriptionsExpanded, setDescriptionsExpanded] = useState(false);
    const coverage = data?.coverage ?? [];
    const [activeTab, setActiveTab] = useState(null);
    const printRef = useRef(null);
    const printJobRef = useRef({ iframe: null, timer: null, cleaned: false, onAfterPrint: null });
    const [forceOpenSections, setForceOpenSections] = useState(false);
    const [copyStatus, setCopyStatus] = useState("");
    const [selectedCompetitors, setSelectedCompetitors] = useState([]);
    const accent = "var(--rusty-orange, #e87722)";
    const isAltDataMode = String(mode || "").trim().toLowerCase() === "altdata";
    const tearsheetAccentVars = {
        "--rusty-orange": isAltDataMode ? "#990026" : "#e87722",
        "--rusty-orange-rgb": isAltDataMode ? "153, 0, 38" : "232, 119, 34",
    };

    const selectedCompetitorIds = useMemo(
        () => selectedCompetitors.map(c => c.id),
        [selectedCompetitors]
    );

    const toggleCompetitor = (comp) => {
        setSelectedCompetitors(prev => {
            const exists = prev.some(c => c.id === comp.id);
            if (exists) {
                return prev.filter(c => c.id !== comp.id);
            }
            return [...prev, { id: comp.id, name: comp.name }];
        });
    };

    const hasNonEmptyHtml = (html) =>
        String(html || "")
            .replace(/<br\s*\/?>/gi, "")
            .replace(/&nbsp;|&#160;/gi, "")
            .replace(/<[^>]*>/g, "")
            .trim().length > 0;

    function renderCategoryCell(items, sanitize) {
        if (!Array.isArray(items) || items.length === 0) return null;

        const html = items
            .filter((s) => s != null && String(s).trim().length > 0)
            .map((s) => `<div class="mb-1">${s}</div>`)
            .join("");

        if (!hasNonEmptyHtml(html)) return null;

        return (
            <td
                className="align-top px-2 py-2 text-sm border"
                dangerouslySetInnerHTML={sanitize(html)}
            />
        );
    }

    function parseSynopsisToBlocks(htmlString) {
        if (!htmlString || typeof htmlString !== "string") {
            return [{ type: "free", html: htmlString || "" }];
        }

        const doc = new DOMParser().parseFromString(htmlString, "text/html");
        const body = doc.body;

        const cleanText = (el) =>
            (el.textContent || "")
                .replace(/\u00a0|&nbsp;/g, " ")
                .replace(/\s+/g, " ")
                .trim();

        // Extract a title (ignoring <br>)
        const getHeaderTitle = (span) => {
            const clone = span.cloneNode(true);
            clone.querySelectorAll("br").forEach((br) => br.remove());
            return cleanText(clone);
        };

        // Heuristic: ignore underline+strong that are basically inline links like "here."
        const isAnchorOnlyDivider = (span) => {
            const hasAnchor = span.querySelector("a") !== null || !!span.closest("a");
            if (!hasAnchor) return false;

            const title = getHeaderTitle(span);
            const plain = title.toLowerCase();

            // very short / “linkish” labels shouldn't be headers
            const short = plain.replace(/[^a-z0-9]+/g, "").length <= 10;

            const commonLinky = /^(here|read more|more|learn more|link|view|details)\.?$/i.test(title);

            // If it’s just an anchor and super short (or a common link word), treat as inline, not a header
            return short || commonLinky;
        };

        const hasMeaningfulTitle = (span) => {
            const title = getHeaderTitle(span);
            // must contain at least 2 letters overall, and not be empty
            if (!title) return false;
            if (!/[A-Za-z].*[A-Za-z]/.test(title)) return false;
            // reject anchor-only dividers
            if (isAnchorOnlyDivider(span)) return false;
            return true;
        };

        // Find underline spans
        const underlineSpans = Array.from(
            body.querySelectorAll('span[style*="text-decoration" i]')
        );

        // Keep ONLY spans that are both underline + strong (inside or wrapper) and meaningful
        const headerSpans = underlineSpans.filter((span) => {
            const style = (span.getAttribute("style") || "").toLowerCase();
            if (!style.includes("underline")) return false;

            const hasStrongInside = span.querySelector("strong, b") !== null;
            const inStrongWrapper = span.closest("strong, b") !== null;
            if (!(hasStrongInside || inStrongWrapper)) return false;

            return hasMeaningfulTitle(span);
        });

        if (headerSpans.length === 0) return [{ type: "free", html: htmlString }];

        // Sort by DOM order
        headerSpans.sort((a, b) => {
            const pos = a.compareDocumentPosition(b);
            if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
            if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
            return 0;
        });

        const rangeHtml = (startSetter, endSetter) => {
            const r = doc.createRange();
            startSetter(r);
            endSetter(r);
            const frag = r.cloneContents();
            const wrap = doc.createElement("div");
            wrap.appendChild(frag);
            return wrap.innerHTML.trim();
        };

        const hasRealText = (html) =>
            (html || "")
                .replace(/<br\s*\/?>/gi, "")
                .replace(/&nbsp;|&#160;/gi, "")
                .replace(/<[^>]*>/g, "")
                .trim().length > 0;

        const blocks = [];
        const boundaryNode = (span) => span; // cut after the underline span itself

        // Content before first header
        const beforeFirst = rangeHtml(
            (r) => r.setStartBefore(body.firstChild ?? body),
            (r) => r.setEndBefore(boundaryNode(headerSpans[0]))
        );
        if (hasRealText(beforeFirst)) blocks.push({ type: "free", html: beforeFirst });

        // Sections
        for (let i = 0; i < headerSpans.length; i++) {
            const curr = headerSpans[i];
            const next = headerSpans[i + 1] || null;

            const title = getHeaderTitle(curr);
            if (!title) continue;

            let sectionHtml = rangeHtml(
                (r) => r.setStartAfter(boundaryNode(curr)),
                (r) => {
                    if (next) r.setEndBefore(boundaryNode(next));
                    else r.setEndAfter(body.lastChild ?? body);
                }
            );

            // Fallback for odd nesting
            if (!hasRealText(sectionHtml)) {
                const parent = curr.parentElement;
                if (parent) {
                    sectionHtml = rangeHtml(
                        (r) => r.setStartAfter(parent),
                        (r) => {
                            if (next) r.setEndBefore(boundaryNode(next));
                            else r.setEndAfter(body.lastChild ?? body);
                        }
                    );
                }
            }

            if (hasRealText(sectionHtml)) {
                blocks.push({ type: "section", title, html: sectionHtml });
            }
        }

        return blocks.length ? blocks : [{ type: "free", html: htmlString }];
    }

    function CollapsibleSynopsis({
        rawHtml,
        forceOpenAll = false,
        interactive = true,
        expandAll = false,
        onSectionCountChange,
        sanitize,
        highlight
    }) {
        const blocks = React.useMemo(() => parseSynopsisToBlocks(rawHtml), [rawHtml]);
        const highlightTerms = React.useMemo(() => buildHighlightTerms(highlight), [highlight]);

        const sectionIndices = React.useMemo(
            () => blocks.map((b, idx) => (b.type === "section" ? idx : -1)).filter((x) => x >= 0),
            [blocks]
        );

        const [openMap, setOpenMap] = React.useState(() =>
            Object.fromEntries(sectionIndices.map((i) => [i, false]))
        );

        React.useEffect(() => {
            // Preserve existing open sections across re-renders/scroll-driven updates.
            setOpenMap((prev) => {
                const next = {};
                sectionIndices.forEach((i) => {
                    next[i] = prev?.[i] ?? false;
                });
                return next;
            });
        }, [sectionIndices]);

        React.useEffect(() => {
            if (typeof onSectionCountChange === "function") {
                onSectionCountChange(sectionIndices.length);
            }
        }, [onSectionCountChange, sectionIndices.length]);

        React.useEffect(() => {
            if (!interactive) return;
            setOpenMap((prev) => {
                const next = {};
                sectionIndices.forEach((i) => {
                    next[i] = !!expandAll;
                });
                return next;
            });
        }, [expandAll, interactive, sectionIndices]);

        const hideSections = !interactive && !forceOpenAll;

        return (
            <div className="space-y-2">
                {blocks.map((b, idx) => {
                    if (b.type === "free") {
                        return <div
                            key={`free-${idx}`}
                            dangerouslySetInnerHTML={sanitize(b.html)}
                        />;
                    }

                    if (hideSections) {
                        const hasText =
                            (b.html || "")
                                .replace(/<br\s*\/?>/gi, "")
                                .replace(/&nbsp;|&#160;/gi, "")
                                .replace(/<[^>]*>/g, "")
                                .trim().length > 0;

                        if (!hasText) return null; // don't render empty sections
                        return (
                            <div
                                key={`sec-${idx}`}
                                className="mt-2"
                                dangerouslySetInnerHTML={sanitize(b.html)}
                            />
                        );
                    }

                    const isOpen = !!openMap[idx];
                    const isVisible = forceOpenAll || isOpen;
                    const containsKeyword = sectionContainsKeyword(b.html, highlightTerms);

                    return (
                        <div key={`sec-${idx}`} className="border rounded-md">
                            <button
                                type="button"
                                className={containsKeyword ? "w-full text-left px-3 py-2 font-semibold flex items-center justify-between hover:bg-gray-50 section-highlight" : "w-full text-left px-3 py-2 font-semibold flex items-center justify-between hover:bg-gray-50"}
                                onClick={() => {
                                    if (!interactive) return;
                                    setOpenMap((m) => ({ ...m, [idx]: !m[idx] }));
                                }}
                                aria-disabled={!interactive}
                            >
                                <span>{b.title}</span>
                                <span className="text-xs">
                {interactive ? (isOpen ? "Hide" : "Show") : "Expand above to toggle"}
              </span>
                            </button>
                            <div
                                className={isVisible ? "px-3 pb-3 block" : "px-3 pb-3 hidden"}
                                dangerouslySetInnerHTML={sanitize(b.html)}
                            />
                        </div>
                    );
                })}
            </div>
        );
    }

    useEffect(() => () => cleanupPrintJob(), []);


    function cleanupPrintJob() {
        const job = printJobRef.current;
        if (!job || job.cleaned) return;
        job.cleaned = true;

        try {
            const w = job.iframe?.contentWindow;
            if (w && job.onAfterPrint) w.removeEventListener('afterprint', job.onAfterPrint);
        } catch {}

        if (job.timer) {
            clearTimeout(job.timer);
            job.timer = null;
        }

        try {
            if (job.iframe && job.iframe.isConnected) {
                job.iframe.remove(); // avoids parentNode.removeChild(...)
            }
        } catch {}

        job.iframe = null;
        job.onAfterPrint = null;
    }


    function printElement(el) {
        return new Promise((resolve) => {
            if (!el) return resolve();

            const iframe = document.createElement('iframe');
            Object.assign(iframe.style, { position:'fixed', right:0, bottom:0, width:'0', height:'0', border:0 });
            document.body.appendChild(iframe);

            // register job
            printJobRef.current = { iframe, timer: null, cleaned: false, onAfterPrint: null };

            const doc = iframe.contentDocument || iframe.contentWindow.document;

            const headStyles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
                .map(n => n.outerHTML).join('\n');

            const html = `<!DOCTYPE html>
<html><head>
<meta charset="utf-8"/>
<base href="${document.baseURI}">
${headStyles}
<style>
  @page { size: A4; margin: 12mm; }
  .no-print { display:none !important; }
  html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .overflow-auto, .overflow-scroll { overflow: visible !important; }
</style>
</head><body>${el.outerHTML}</body></html>`;

            doc.open(); doc.write(html); doc.close();

            const w = iframe.contentWindow;

            const onAfterPrint = () => {
                cleanupPrintJob();
                resolve();
            };
            printJobRef.current.onAfterPrint = onAfterPrint;

            try { w.addEventListener('afterprint', onAfterPrint, { once: true }); } catch {}

            printJobRef.current.timer = setTimeout(() => {
                cleanupPrintJob();
                resolve();
            }, 4000);

            setTimeout(() => {
                try { w.focus(); w.print(); }
                catch { cleanupPrintJob(); resolve(); }
            }, 200);
        });
    }


    const handlePrint = () => {
        const prevSyn = synopsisExpanded;
        const prevDesc = descriptionsExpanded;
        setSynopsisExpanded(true);
        setDescriptionsExpanded(true);
        setForceOpenSections(true);
        setTimeout(() => {
            printElement(printRef.current).finally(() => {
                setSynopsisExpanded(prevSyn);
                setDescriptionsExpanded(prevDesc);
                setForceOpenSections(false);
            });
        }, 50);
    };
    const hasVisibleHtml = (html) => {
        if (!html) return false;
        const textOnly = String(html)
            .replace(/<br\s*\/?>/gi, "")
            .replace(/&nbsp;|&#160;/gi, "")
            .replace(/<[^>]*>/g, "")
            .trim();
        return textOnly.length > 0;
    };

    function ensureListMarkers(html) {
        const doc = new DOMParser().parseFromString(String(html || ""), "text/html");
        const body = doc.body;

        body.querySelectorAll("ul").forEach((ul) => {
            ul.classList.add("list-disc", "pl-5", "space-y-1");
            ul.style.setProperty("list-style-type", "disc", "important");
            ul.style.setProperty("padding-left", "1.25rem", "important");
            ul.style.setProperty("margin", "0.25rem 0", "important");
        });

        body.querySelectorAll("ol").forEach((ol) => {
            ol.classList.add("list-decimal", "pl-5", "space-y-1");
            ol.style.setProperty("list-style-type", "decimal", "important");
            ol.style.setProperty("padding-left", "1.25rem", "important");
            ol.style.setProperty("margin", "0.25rem 0", "important");
        });

        body.querySelectorAll("li").forEach((li) => {
            li.style.setProperty("margin", "0.15rem 0", "important");
        });

        return body.innerHTML;
    }

    const createListMarkup = (html) => {
        const decorated = ensureListMarkers(html);

        const doc = new DOMParser().parseFromString(decorated || "", "text/html");
        const tags = Array.from(doc.body.querySelectorAll("*")).map(el => el.tagName.toLowerCase());

        return sanitizeAndHighlight(decorated, highlight, {
            ALLOWED_TAGS: [...new Set([...tags, "mark", "ul", "ol", "li"])],
            ALLOWED_ATTR: ["href", "target", "style", "class"],
        });
    };


    const safeFileBase = (data?.productName || "tearsheet").replace(/[\\/:*?"<>|]/g, "-");

    const getFilenameFromDisposition = (cdHeader) => {
        if (!cdHeader) return null;
        const starMatch = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(cdHeader);
        if (starMatch) return decodeURIComponent(starMatch[1]);
        const plainMatch = /filename\s*=\s*"([^"]+)"/i.exec(cdHeader);
        if (plainMatch) return plainMatch[1];
        const bareMatch = /filename\s*=\s*([^;]+)/i.exec(cdHeader);
        if (bareMatch) return bareMatch[1].trim();
        return null;
    };

    const buildExportUrl = (format /* 'pdf' | 'docx' */) => {
        const base = `${process.env.REACT_APP_API_URL}/exports/products/${productId}`;
        const params = new URLSearchParams();
        params.set("mode", format);
        params.set("ispdf", String(format === "pdf"));
        params.set("isword", String(format === "docx"));
        return `${base}?${params.toString()}`;
    };

    const buildPricingExcelUrl = (pricingType /* 'currentMarket' | 'pricing' */) => {
        const base = `${process.env.REACT_APP_API_URL}/exports/products/${productId}/pricing`;
        const params = new URLSearchParams();
        params.set("format", "xls");
        params.set("type", pricingType);
        return `${base}?${params.toString()}`;
    };

    const downloadPricingExcel = async (pricingType) => {
        try {
            const token = localStorage.getItem("token");
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            const url = buildPricingExcelUrl(pricingType);
            const res = await axios.get(url, { headers, responseType: "blob" });

            const fromHeader = getFilenameFromDisposition(res.headers["content-disposition"]);
            const fallback = `${safeFileBase}-pricing.xls`; // or .xlsx if your backend returns xlsx
            const filename = fromHeader || fallback;

            const blob = new Blob([res.data], { type: "application/vnd.ms-excel" });

            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(link.href);
        } catch (err) {
            console.error("Pricing Excel export failed:", err);
            alert("Sorry—couldn’t export pricing to Excel. Please try again or contact support.");
        }
    };

    const downloadFromServer = async (format /* 'pdf' | 'docx' */) => {
        try {
            const token = localStorage.getItem("token");
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const url = buildExportUrl(format);

            const res = await axios.get(url, { headers, responseType: "blob" });

            const fromHeader = getFilenameFromDisposition(res.headers["content-disposition"]);
            const fallback = `${safeFileBase}.${format === "pdf" ? "pdf" : "docx"}`;
            const filename = fromHeader || fallback;

            const blob = new Blob([res.data], {
                type:
                    format === "pdf"
                        ? "application/pdf"
                        : "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            });

            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(link.href);
        } catch (err) {
            console.error("Export failed:", err);
            alert("Sorry—couldn’t export the tearsheet. Please try again or contact support.");
        }
    };

    const copyTearsheetText = async () => {
        const text = printRef.current?.innerText?.trim() || "";
        if (!text) {
            setCopyStatus("No text");
            return;
        }
        try {
            if (navigator.clipboard?.writeText && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
            } else {
                const ta = document.createElement("textarea");
                ta.value = text;
                ta.setAttribute("readonly", "");
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.focus();
                ta.select();
                const ok = document.execCommand("copy");
                document.body.removeChild(ta);
                if (!ok) throw new Error("Copy command failed");
            }
            setCopyStatus("Copied");
        } catch (err) {
            console.error("Copy tearsheet text failed:", err);
            setCopyStatus("Copy failed");
        }
    };

    useEffect(() => {
        if (!copyStatus) return undefined;
        const timer = setTimeout(() => setCopyStatus(""), 1500);
        return () => clearTimeout(timer);
    }, [copyStatus]);

    useEffect(() => {
        if (coverage.length > 0) {
            setActiveTab(coverage[0].moduleName);
        } else {
            setActiveTab(null);
        }
    }, [data]);

    const activeData = useMemo(() => {
        return coverage.find(c => c.moduleName === activeTab);
    }, [coverage, activeTab]);

    // Fetch product data
    useEffect(() => {
        if (!productId) return;
        let isMounted = true;
        setLoading(true);
        setError(null);

        async function fetchTearsheet() {
            try {
                const token = localStorage.getItem("token");
                const headers = { Authorization: `Bearer ${token}` };
                const res = await axios.get(
                    `${contextPath}/product/${productId}/tearsheet`,
                    { headers, params: { mode } }
                );
                if (isMounted) setData(res.data);
            } catch (err) {
                console.error("Tearsheet fetch failed:", err);
                if (isMounted) setError(err);
            } finally {
                if (isMounted) setLoading(false);
            }
        }

        fetchTearsheet();
        return () => {
            isMounted = false;
        };
    }, [productId, contextPath, mode]);

    // ALL hooks above, ALL early returns below.
    if (loading) return <div className="loading-spinner" />;
    if (error) return <div className="p-4 text-red-600">Error: {error.message}</div>;
    if (!data) return null;

    // Destructure data only after confirming data is present!
    const {
        productName,
        vendorNames = [],
        lastUpdated,
        synopsisHtml,
        categories,
        pricing = [],
        currentMarketPricing = [],
        strengthsHtml,
        weaknessesHtml,
        descriptionsHtml,
        competitors,
    } = data;

    const createMarkup = (html) => {
        const doc = new DOMParser().parseFromString(html || "", "text/html");
        const tags = Array.from(doc.body.querySelectorAll("*")).map(el => el.tagName.toLowerCase());

        return sanitizeAndHighlight(html, highlight, {
            ALLOWED_TAGS: [...new Set([...tags, "mark"])],
            ALLOWED_ATTR: ["href", "target", "style", "class"],
        });
    };


    const formattedDate = lastUpdated
        ? new Date(lastUpdated).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        })
        : "";

    return (
        <div ref={printRef} className="Threedi-blue overflow-auto bg-white shadow rounded" style={tearsheetAccentVars}  onClick={onGlossaryClick}>
            <div className="last-edited">Last Edited: {formattedDate}</div>

            <div className="p-4 pb-0 no-print">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold">
  <span className="block text-gray-500 text-sm uppercase tracking-wide">
    Product Tearsheet
  </span>
                        {productName}
                    </h2>

                    <div className="flex items-center gap-2" role="toolbar" aria-label="Export actions">
                        {/* Print (browser) */}
                        <button
                            type="button"
                            onClick={handlePrint}
                            className="inline-flex items-center justify-center w-10 h-10 rounded-full border"
                            style={{ borderColor: accent, color: accent }}
                            title="Print this tearsheet"
                            aria-label="Print this tearsheet"
                        >
                            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                                <path d="M6 9V4h12v5" />
                                <rect x="4" y="9" width="16" height="8" rx="2" ry="2" />
                                <path d="M7 17h10v3H7z" />
                            </svg>
                        </button>

                        <button
                            type="button"
                            onClick={copyTearsheetText}
                            className="inline-flex items-center justify-center w-10 h-10 rounded-full border"
                            style={{ borderColor: accent, color: accent }}
                            title="Copy tearsheet text"
                            aria-label="Copy tearsheet text"
                        >
                            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                                <rect x="9" y="9" width="11" height="11" rx="2" />
                                <path d="M6 15H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1" />
                            </svg>
                        </button>


                        {/* Word (server) */}
                        <button
                            type="button"
                            onClick={() => downloadFromServer("docx")}
                            className="inline-flex items-center justify-center w-10 h-10 rounded-full border"
                            style={{ borderColor: accent, color: accent }}
                            title="Export Word (.docx)"
                            aria-label="Export Word (.docx)"
                        >
                            {/* File-Word icon */}
                            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                                <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z" fill="none" stroke="currentColor" strokeWidth="1.6"/>
                                <path d="M14 2v5h5" fill="none" stroke="currentColor" strokeWidth="1.6"/>
                                <g fontFamily="Segoe UI, Arial, sans-serif" fontWeight="700">
                                    <text x="7.2" y="17" fontSize="8" fill="currentColor">W</text>
                                </g>
                            </svg>
                        </button>

                        {/* PDF (server) */}
                        <button
                            type="button"
                            onClick={() => downloadFromServer("pdf")}
                            className="inline-flex items-center justify-center w-10 h-10 rounded-full border"
                            style={{ borderColor: accent, color: accent }}
                            title="Export PDF"
                            aria-label="Export PDF"
                        >
                            {/* File-PDF icon */}
                            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                                <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z" fill="none" stroke="currentColor" strokeWidth="1.6"/>
                                <path d="M14 2v5h5" fill="none" stroke="currentColor" strokeWidth="1.6"/>
                                <g fontFamily="Segoe UI, Arial, sans-serif" fontWeight="800">
                                    <text x="5.6" y="17" fontSize="6.6" letterSpacing="0.3" fill="currentColor">PDF</text>
                                </g>
                            </svg>
                        </button>
                    </div>
                </div>
                {copyStatus && (
                    <div className="mt-1 text-xs text-right text-gray-600" aria-live="polite">
                        {copyStatus}
                    </div>
                )}
            </div>
            <div className="p-4 overflow-auto">
                {/* Vendors */}
                {vendorNames.length > 0 && (
                    <section className="mb-2">
            <span className="font-semibold tearsheet-heading">
              Vendor{vendorNames.length > 1 ? "s" : ""}:
            </span>{" "}
                        {vendorNames.join(", ")}
                    </section>
                )}

                <hr className="my-2" />

                {/* Synopsis */}
                <section className="mb-6">
                    <div className="flex items-center justify-between gap-3">
                    <h3 className="font-semibold tearsheet-heading">Synopsis</h3>
                        {synopsisExpanded && synopsisSectionCount > 0 && (
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    className="view-more synopsis-bulk-toggle"
                                    onClick={() => setSynopsisExpandAll(true)}
                                >
                                    Expand all
                                </button>
                                <button
                                    type="button"
                                    className="view-more synopsis-bulk-toggle"
                                    onClick={() => setSynopsisExpandAll(false)}
                                >
                                    Collapse all
                                </button>
                            </div>
                        )}
                    </div>

                    <div className={`mt-2 text-sm ${!synopsisExpanded ? "line-clamp-5" : ""}`}>
                        <CollapsibleSynopsis
                            rawHtml={synopsisHtml}
                            forceOpenAll={forceOpenSections}
                            interactive={synopsisExpanded}
                            expandAll={synopsisExpandAll}
                            onSectionCountChange={setSynopsisSectionCount}
                            highlight={highlight}
                            sanitize={(html) => createMarkup(html)}
                        />
                    </div>

                    <span className="view-more" onClick={() => setSynopsisExpanded((v) => !v)}>
    {synopsisExpanded ? "View Less" : "View More"}
  </span>
                </section>

                {/* Categories */}
                {Array.isArray(categories) && categories.some(c => Array.isArray(c.items) && c.items.length) && (
                    <section className="mb-6">
                        <hr className="my-2" />
                        <h3 className="font-semibold tearsheet-heading mb-2">Categories</h3>

                        <div className="overflow-auto">
                            <table className="min-w-full border-collapse">
                                <thead>
                                <tr>
                                    {categories
                                        .filter(c => Array.isArray(c.items) && c.items.length > 0)
                                        .map((c) => (
                                            <th
                                                key={c.name}
                                                className="px-2 py-2 text-left border font-semibold align-middle whitespace-nowrap"
                                            >
                                                {c.name}
                                            </th>
                                        ))}
                                </tr>
                                </thead>
                                <tbody>
                                <tr>
                                    {categories
                                        .filter(c => Array.isArray(c.items) && c.items.length > 0)
                                        .map((c) => (
                                            <React.Fragment key={`cell-${c.name}`}>
                                                {renderCategoryCell(c.items, (html) => sanitizeAndHighlight(html, highlight))}
                                            </React.Fragment>
                                        ))}
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>
                )}

                {currentMarketPricing.length > 0 && (
                    <section className="mb-6">
                        <hr className="my-2" />
                        <div className="flex items-center justify-between">
                            <h3 className="font-semibold tearsheet-heading">Current Market Pricing</h3>

                            <button
                                type="button"
                                onClick={() => downloadPricingExcel("currentMarket")}
                                className="no-print inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-semibold"
                                style={{ borderColor: accent, color: accent }}
                                title="Export pricing to Excel"
                            >
                                <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                                    <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z" fill="none" stroke="currentColor" strokeWidth="1.6"/>
                                    <path d="M14 2v5h5" fill="none" stroke="currentColor" strokeWidth="1.6"/>
                                    <g fontFamily="Segoe UI, Arial, sans-serif" fontWeight="800">
                                        <text x="6.2" y="17" fontSize="7" fill="currentColor">XLS</text>
                                    </g>
                                </svg>
                                Export
                            </button>
                        </div>
                        <table className="min-w-full border-collapse text-sm">
                            <thead>
                            <tr>
                                <th className="border px-2 py-1 text-left">Product/Component Name</th>
                                <th className="border px-2 py-1">Currency</th>
                                <th className="border px-2 py-1">Min</th>
                                <th className="border px-2 py-1">Max</th>
                                <th className="border px-2 py-1">Price Category</th>
                                <th className="border px-2 py-1">Term</th>
                                <th className="border px-2 py-1">Description</th>
                            </tr>
                            </thead>
                            <tbody>
                            {currentMarketPricing.map((p, i) => (
                                <tr key={`cmp-${i}`}>
                                    <td className="border px-2 py-1">{p.component}</td>
                                    <td className="border px-2 py-1 text-center">{p.currency}</td>
                                    <td className="border px-2 py-1 text-right">{p.minimum}</td>
                                    <td className="border px-2 py-1 text-right">{p.maximum}</td>
                                    <td className="border px-2 py-1">{p.priceCategory}</td>
                                    <td className="border px-2 py-1">{p.term}</td>
                                    <td
                                        className="border px-2 py-1"
                                        dangerouslySetInnerHTML={createMarkup(p.comments)}
                                    />
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </section>
                )}

                {/* Pricing */}
                {(currentMarketPricing?.length ?? 0) === 0 &&
                    (pricing?.length ?? 0) > 0 && (
                        <section className="mb-6">
                        <hr className="my-2" />
                            <div className="flex items-center justify-between">
                                <h3 className="font-semibold tearsheet-heading">Pricing</h3>

                                <button
                                    type="button"
                                    onClick={() => downloadPricingExcel("pricing")}
                                    className="no-print inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-semibold"
                                    style={{ borderColor: accent, color: accent }}
                                    title="Export pricing to Excel"
                                >
                                    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                                        <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z" fill="none" stroke="currentColor" strokeWidth="1.6"/>
                                        <path d="M14 2v5h5" fill="none" stroke="currentColor" strokeWidth="1.6"/>
                                        <g fontFamily="Segoe UI, Arial, sans-serif" fontWeight="800">
                                            <text x="6.2" y="17" fontSize="7" fill="currentColor">XLS</text>
                                        </g>
                                    </svg>
                                    Export
                                </button>
                            </div>
                        <table className="min-w-full border-collapse text-sm">
                            <thead>
                            <tr>
                                <th className="border px-2 py-1 text-left">Component</th>
                                <th className="border px-2 py-1">Currency</th>
                                <th className="border px-2 py-1">Min</th>
                                <th className="border px-2 py-1">Max</th>
                                <th className="border px-2 py-1">Price Category</th>
                                <th className="border px-2 py-1">Term</th>
                                <th className="border px-2 py-1">Comments</th>
                            </tr>
                            </thead>
                            <tbody>
                            {pricing.map((p, i) => (
                                <tr key={i}>
                                    <td className="border px-2 py-1">{p.component}</td>
                                    <td className="border px-2 py-1 text-center">{p.currency}</td>
                                    <td className="border px-2 py-1 text-right">{p.minimum}</td>
                                    <td className="border px-2 py-1 text-right">{p.maximum}</td>
                                    <td className="border px-2 py-1">{p.priceCategory}</td>
                                    <td className="border px-2 py-1">{p.term}</td>
                                    <td
                                        className="border px-2 py-1"
                                        dangerouslySetInnerHTML={createMarkup(p.comments)}
                                    />
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </section>
                )}

                <hr className="my-2" />

                {/* Strengths & Weaknesses */}
                <div className="grid grid-cols-2 gap-6 mb-6">
                    {strengthsHtml && (
                        <div>
                            <h3 className="font-semibold tearsheet-heading">Strengths</h3>
                            <div
                                className="mt-2 text-sm"
                                dangerouslySetInnerHTML={createListMarkup(strengthsHtml)}
                            />
                        </div>
                    )}
                    {weaknessesHtml && (
                        <div>
                            <h3 className="font-semibold tearsheet-heading">Weaknesses</h3>
                            <div
                                className="mt-2 text-sm"
                                dangerouslySetInnerHTML={createListMarkup(weaknessesHtml)}

                            />
                        </div>
                    )}
                </div>


                {/* Coverage Summary */}
                <section className="mb-6">
                    <h3 className="font-semibold tearsheet-heading">Coverage Summary:</h3>
                    <div className="flex space-x-2 my-4">
                        {coverage.map((c) => (
                            <button
                                key={c.moduleName}
                                onClick={() => setActiveTab(c.moduleName)}
                                className={`px-4 py-2 rounded border font-small ${
                                    activeTab === c.moduleName ? "text-white" : "bg-gray-200 text-black"
                                }`}
                                style={
                                    activeTab === c.moduleName
                                        ? { backgroundColor: accent }
                                        : {}
                                }
                            >
                                {c.moduleName}
                            </button>
                        ))}
                    </div>
                    {activeData && (
                        <div className="grid grid-cols-2 gap-6">
                            <div>
                                <h5 className="font-semibold text-black mb-2">Data Items</h5>
                                <ul className="list-none">
                                    {activeData.dataItems.map((it, idx) => (
                                        <li
                                            key={idx}
                                            className="flex justify-between py-1 border-b text-sm"
                                        >
                                            <span>{it.name}</span>
                                            <span>
                                              {it.available ? (
                                                  // Simple tick, rusty orange
                                                  <svg
                                                      width="18"
                                                      height="18"
                                                      viewBox="0 0 18 18"
                                                      fill="none"
                                                      style={{ color: accent }}
                                                      xmlns="http://www.w3.org/2000/svg"
                                                  >
                                                      <path
                                                          d="M4 9.5l3.5 3.5L14 6"
                                                          stroke="currentColor"
                                                          strokeWidth="2.3"
                                                          strokeLinecap="round"
                                                          strokeLinejoin="round"
                                                      />
                                                  </svg>
                                              ) : (
                                                  <svg
                                                      width="18"
                                                      height="18"
                                                      viewBox="0 0 18 18"
                                                      fill="none"
                                                      style={{color: 'var(--bs-btn-bg)'}}
                                                      xmlns="http://www.w3.org/2000/svg"
                                                  >
                                                      <path
                                                          d="M5 5l8 8M13 5l-8 8"
                                                          stroke="currentColor"
                                                          strokeWidth="2.3"
                                                          strokeLinecap="round"
                                                      />
                                                  </svg>
                                              )}
                                            </span></li>
                                    ))}
                                </ul>
                            </div>
                            <div>
                                <h5 className="font-semibold text-black mb-2">Functionality Items</h5>
                                <ul className="list-none">
                                    {activeData.functionalityItems.map((it, idx) => (
                                        <li
                                            key={idx}
                                            className="flex justify-between py-1 border-b  text-sm"
                                        >
                                            <span>{it.name}</span>
                                            <span>
                                          {it.available ? (
                                              // Simple tick, rusty orange
                                              <svg
                                                  width="18"
                                                  height="18"
                                                  viewBox="0 0 18 18"
                                                  fill="none"
                                                  style={{ color: accent }}
                                                  xmlns="http://www.w3.org/2000/svg"
                                              >
                                                  <path
                                                      d="M4 9.5l3.5 3.5L14 6"
                                                      stroke="currentColor"
                                                      strokeWidth="2.3"
                                                      strokeLinecap="round"
                                                      strokeLinejoin="round"
                                                  />
                                              </svg>
                                          ) : (
                                              // Simple X, btn bg color
                                              <svg
                                                  width="18"
                                                  height="18"
                                                  viewBox="0 0 18 18"
                                                  fill="none"
                                                  style={{color: 'var(--bs-btn-bg)'}}
                                                  xmlns="http://www.w3.org/2000/svg"
                                              >
                                                  <path
                                                      d="M5 5l8 8M13 5l-8 8"
                                                      stroke="currentColor"
                                                      strokeWidth="2.3"
                                                      strokeLinecap="round"
                                                  />
                                              </svg>
                                          )}
                                        </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}
                </section>


                {/* Key Competitors */}
                {Array.isArray(competitors) && competitors.length > 0 && (
                    <section className="mb-6">
                        <hr className="my-2" />
                        <h3 className="font-semibold tearsheet-heading mb-2">Key Competitors:</h3>
                        <table className="min-w-full border-collapse">
                            <tbody>
                            {competitors.map((comp, idx) => (
                                <tr key={comp.id} className={idx % 2 === 1 ? "bg-gray-50" : ""}>
                                    {/* Checkbox */}
                                    <td className="align-top pr-2 pt-1">
                                        <input
                                            type="checkbox"
                                            checked={selectedCompetitorIds.includes(comp.id)}
                                            onChange={() => toggleCompetitor(comp)}
                                        />

                                    </td>
                                    {/* Competitor name & vendors */}
                                    <td className="align-top py-1">
                                        {/* Product link */}
                                        <a
                                            href="#"
                                            className="font-bold text-blue-900 hover:underline text-sm"
                                            onClick={e => {
                                                e.preventDefault();
                                                if (onOpenProductPopup) onOpenProductPopup(comp.id, comp.name);
                                            }}
                                        >
                                            {comp.name}
                                        </a>
                                        {/* Vendors */}
                                        {comp.vendors && comp.vendors.length > 0 && (
                                            <>
                                                &nbsp;<b>(</b>
                                                {comp.vendors.map((v, vi) => (
                                                    <span key={v.id}>
                                        {vi > 0 && <b>,&nbsp;</b>}
                                                        <a
                                                            href="#"
                                                            className="hover:underline text-sm"
                                                            onClick={e => {
                                                                e.preventDefault();
                                                                if (onOpenVendorPopup) onOpenVendorPopup(v.id, v.name);
                                                            }}
                                                        >
                                            <b>{v.name}</b>
                                        </a>
                                    </span>
                                                ))}
                                                <b>)</b>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                        {selectedCompetitorIds.length > 0 && (
                            <div
                                className="mt-3 p-3 rounded-xl border text-xs"
                                style={{
                                    borderColor: "rgba(var(--rusty-orange-rgb, 232,119,34), 0.30)",
                                    backgroundColor: "rgba(var(--rusty-orange-rgb, 232,119,34), 0.06)",
                                }}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <div className="font-semibold text-[var(--rusty-orange)]">
                                        Compare Data with {productName}
                                    </div>

                                </div>

                                <div className="flex flex-wrap gap-2">
                                    {/* VIEW COMPARISON */}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const ids = [productId, ...selectedCompetitorIds];
                                            // use the same event the dashboard uses
                                            window.dispatchEvent(
                                                new CustomEvent("open-profiler-data-modal", {
                                                    detail: ids.join(","),
                                                })
                                            );
                                        }}
                                        className="px-3 py-1.5 rounded-lg border border-[var(--threedi-blue,#25408f)] text-[var(--threedi-blue,#25408f)] text-xs font-semibold hover:bg-[var(--threedi-blue,#25408f)]/5"
                                    >
                                        View comparison
                                    </button>

                                    {/* SAVE COMPARISON */}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const ids = [productId, ...selectedCompetitorIds];
                                            window.dispatchEvent(
                                                new CustomEvent("profiler:save-product-comparison", {
                                                    detail: { productIds: ids },
                                                })
                                            );
                                        }}
                                        className="px-3 py-1.5 rounded-lg border border-[var(--rusty-orange)] text-[var(--rusty-orange)] text-xs font-semibold hover:bg-[var(--rusty-orange)]/10"
                                    >
                                        Save comparison
                                    </button>
                                </div>
                            </div>
                        )}

                    </section>
                )}




                {/* Detailed Descriptions */}
                {hasVisibleHtml(descriptionsHtml) && (
                    <section className="mb-6">
                        <hr className="my-2" />
                        <h3 className="font-semibold tearsheet-heading">
                            Detailed Descriptions
                        </h3>
                        <div
                            className={`mt-2 text-sm ${
                                !descriptionsExpanded ? "line-clamp-3" : ""
                            }`}
                            dangerouslySetInnerHTML={createMarkup(descriptionsHtml)}
                        />
                        <span
                            className="view-more"
                            onClick={() => setDescriptionsExpanded((v) => !v)}
                        >
              {descriptionsExpanded ? "View Less" : "View More"}
            </span>
                    </section>
                )}

                {/* Notes & Commentary */}
                <NotesAndCommentary productId={productId} productName={productName} contextPath={contextPath} />

                <div className="sticky bottom-0 bg-white pt-4 mt-6 border-t">
                    <div className="flex items-center justify-end gap-3">
                        <button
                            type="button"
                            onClick={() => onOpenDataModal?.(productId)}
                            className="
                px-4 py-2 rounded-2xl font-semibold
                text-white
              "
                            style={{ backgroundColor: accent, borderColor: accent }}
                            title="Open Data modal"
                        >
                            View Data
                        </button>

                        <button
                            type="button"
                            onClick={() => onOpenFunctionalityModal?.(productId)}
                            className="
                px-4 py-2 rounded-2xl font-semibold
                border text-white
                hover:bg-[#1f367a] transition
              "
                            style={{ backgroundColor: accent, borderColor: accent }}
                            title="Open Functionality modal"
                        >
                            View Functionality
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
